'use client';

import { useEffect, useMemo } from 'react';
import { ReactComponent as CommentIcon } from '@assets/icons/comment.svg';
import config from 'config';
import { ROUTES } from 'constant';
import { useRouter } from 'next/navigation';
import { useGetClAppointmentsQuery } from 'services/userProfileApi';

import { Box, Flex, Heading, Stack, Text, useTheme } from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  resetForm,
  setAppointment,
  setStep
} from 'store/reducers/bookingAppointmentSlice';

import { mergeArrays } from 'utils/mergeArrays';

import { AppointmentsType, BookingSteps } from 'types';

import { AddToCalendar } from 'components';
import { Button, Divider } from 'ui';
import { AppointmentDetails, ProductList, ReviewList } from './components';

const {
  isAddToCalendarAvailable,
  booking: {
    success: { title, titleMove, description }
  }
} = config;

interface SuccessStepProps {
  handleReserveAgain: () => void;
}

const SuccessStep = ({ handleReserveAgain }: SuccessStepProps) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const {
    colors: {
      palette: {
        booking: { stepSuccess }
      }
    }
  } = useTheme();
  const { refetch } = useGetClAppointmentsQuery({
    itemType: AppointmentsType.future
  });

  const {
    checkoutData,
    selectedAppointment,
    apptCalendarDate,
    comments,
    promoCode,
    promoData,
    moveApptId,
    selectedProducts,
    recommendedProducts
  } = useAppSelector((state) => state.bookingAppointment);
  const { user, selectedLocation } = useAppSelector((state) => state.user);

  const userName = user ? user.nickname : '';

  const handleRepeatAppointment = () => {
    dispatch(setAppointment(null));
    dispatch(setStep(2));
  };

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (!selectedAppointment || !user) {
      router.push(ROUTES.booking);
    }
    return () => {
      dispatch(resetForm());
      dispatch(setStep(BookingSteps.SERVICE));
    };
  }, [router, selectedAppointment, user, dispatch]);

  const slelectedProductId = selectedProducts.map((product) => product.id);
  const productList = recommendedProducts.filter((product) =>
    slelectedProductId.includes(product.id)
  );

  const mergedList = mergeArrays(productList, selectedProducts);

  const productSum = selectedProducts.reduce(
    (sum, product) => sum + product.qty * product.price,
    0
  );

  const providerNames = useMemo(
    () =>
      selectedAppointment?.services
        .flatMap((service) =>
          (service?.assigned_em_2 ?? []).map((em) => em?.em_name ?? '')
        )
        .join(', '),
    [selectedAppointment]
  );

  return (
    <Stack w='100%' mx='auto' gap={0}>
      <Stack
        w='100%'
        gap='0'
        mb={{ base: '18px', md: '20px' }}
        mt={{ base: '25px', md: '40px' }}
      >
        <Heading
          textStyle='h1'
          as='h1'
          textAlign='center'
          color={stepSuccess.headingColor}
          fontSize={{ base: '28px', md: '36px', lg: '42px', xl: '45px' }}
          fontWeight='400'
          mb='28px'
        >
          {moveApptId ? titleMove : title}
        </Heading>

        {description && (
          <Text
            textAlign='center'
            color={stepSuccess.descriptionColor}
            fontSize={{ base: '18px', md: '21px' }}
            lineHeight='160%'
            mb={{ base: '14px', md: '30px', lg: '23px' }}
          >
            {`Thank you, ${userName}. ${description}`}
          </Text>
        )}
        <Divider top='0' h='1px' bg={stepSuccess.dividerColor} border='none' />
      </Stack>

      {checkoutData && selectedLocation && selectedAppointment && (
        <>
          <AppointmentDetails
            selectedLocation={selectedLocation}
            selectedAppointment={selectedAppointment}
            promoData={promoData}
            data={checkoutData}
            productSum={productSum}
          />
          <Divider
            h='2px'
            bg={stepSuccess.headerDividerColor}
            my='10px'
            border='none'
          />
        </>
      )}

      <Box>
        {checkoutData && selectedAppointment && (
          <ReviewList
            data={checkoutData}
            selectedAppointment={selectedAppointment}
            promoData={promoData}
            promoCode={promoCode}
          />
        )}

        {productList && <ProductList list={mergedList} />}
      </Box>

      <Divider h='1px' bg={stepSuccess.dividerColor} mt='20px' border='none' />
      {comments && (
        <>
          <Flex gap={{ base: '20px', md: '25px' }} my='20px' px='15px'>
            <CommentIcon
              width={35}
              height={35}
              fill={stepSuccess.commentIconColor}
              style={{ flexShrink: 0 }}
            />
            <Text
              fontSize={{ base: '16px', md: '18px', lg: '22px' }}
              mt={{ base: '6px', md: '5px', lg: 0 }}
              lineHeight='140%'
              color={stepSuccess.commentTextColor}
            >
              {comments}
            </Text>
          </Flex>
          <Divider h='1px' bg={stepSuccess.dividerColor} border='none' />
        </>
      )}

      <Stack gap={{ base: '30px', md: '35px' }}>
        <Flex
          direction={{ base: 'column-reverse', md: 'row' }}
          mt='50px'
          justifyContent='center'
          alignItems='center'
          gap='20px'
          w='100%'
        >
          <AddToCalendar
            providerNames={providerNames ?? ''}
            appointmentName={selectedAppointment?.name}
            appointmenStartDate={apptCalendarDate.start}
            appointmenEndDate={apptCalendarDate.end}
            location={
              selectedLocation?.addr1
                ? `${selectedLocation?.addr1}, ${selectedLocation?.city}, ${selectedLocation?.province} ${selectedLocation?.postal}, ${selectedLocation?.country}`
                : ''
            }
            tzOffset={selectedAppointment?.tz_offset ?? 0}
            isDefault={!isAddToCalendarAvailable}
          />
          <Button variant='outline' w='272px' onClick={handleReserveAgain}>
            Reserve Another
          </Button>
        </Flex>

        <Button
          variant='text'
          onClick={handleRepeatAppointment}
          textDecoration='underline'
          mb='-10px'
        >
          Repeat this appointment
        </Button>
      </Stack>
    </Stack>
  );
};

export default SuccessStep;
